import { IFile } from 'app/interfaces/IFile';
import { LoggerService } from 'app/logger/logger.service';
import { normalizePath, joinPaths, getEnvPathSlash, convertObjectToString } from 'app/shared/utils/string.util';
import * as fs from 'fs';
import { readdir, readFile } from 'fs';
import { promisify } from 'util';
import { getPipelineActivitiesByType } from 'shell-models-lib';

export interface ISynapsePool {
    poolType: string;
    poolFiles: IFile[];
}

export class LocalFileUtils {
    private static readonly className = 'LocalFileUtils';

    public static writeFiles(loggerService: LoggerService, files: IFile[], outputFolder: string = '', rootFolder: string = null): void {
        const baseOutputPath: string = rootFolder ?? process.cwd();
        files.forEach((file) => {
            try {
                const fullFilePath = normalizePath(joinPaths(baseOutputPath, outputFolder, file.fileName));
                loggerService.log(this.className, `Saving file: ${fullFilePath}`);
                const dir = fullFilePath.substring(0, fullFilePath.lastIndexOf(getEnvPathSlash()));
                if (!fs.existsSync(dir)) {
                    loggerService.log(this.className, `Dir: ${dir} does not exist, creating it....`);
                    fs.mkdirSync(dir, { recursive: true });
                }
                fs.writeFileSync(fullFilePath, file.payload);
                loggerService.log(this.className, 'File saved.');
            } catch (error) {
                loggerService.warn(this.className, `Unexpected error when writing file ${file.fileName}, error: ${convertObjectToString(error)}`);
            }
        });
    }

    public static async generateSynapsePoolFiles(loggerService: LoggerService, rootFolder: string): Promise<ISynapsePool[]> {
        const sparkPools: string[] = [];
        const sqlPools: string[] = [];
        const sparkPoolFiles: IFile[] = [];
        const sqlPoolFiles: IFile[] = [];
        const synapsePools: ISynapsePool[] = [];
        const pipelineFiles: string[] = await LocalFileUtils.readArtifactFiles(loggerService, rootFolder, 'pipeline');
        pipelineFiles.forEach((pipelineJson) => {
            const pipelineObject = JSON.parse(pipelineJson);

            // Get all SynapseNotebook', 'SparkJob' activities under current pipeline
            const activityTypes: string[] = ['SynapseNotebook', 'SparkJob'];
            const activities = JSON.parse(getPipelineActivitiesByType(pipelineJson, activityTypes));

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            activities.forEach((activity: any) => {
                const sparkPoolReferenceName =
                    activity.typeProperties.sparkPool?.referenceName ?? activity.typeProperties.targetBigDataPool?.referenceName;
                if (sparkPoolReferenceName && typeof sparkPoolReferenceName === 'string' && !sparkPools.includes(sparkPoolReferenceName)) {
                    sparkPools.push(sparkPoolReferenceName);
                    const bigDataPoolFile: IFile = {
                        payload: JSON.stringify({
                                name: sparkPoolReferenceName,
                                properties: {},
                            }, null, '\t'),
                        fileName: `${sparkPoolReferenceName}.json`
                    };
                    sparkPoolFiles.push(bigDataPoolFile);
                }
            });

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const sqlPoolStoredProcActivities = pipelineObject.properties.activities.filter((activity: any) => activity.type === 'SqlPoolStoredProcedure');
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            sqlPoolStoredProcActivities.forEach((storedProcActivity: any) => {
                const sqlPoolReferenceName = storedProcActivity.sqlPool?.referenceName;
                if (sqlPoolReferenceName && typeof sqlPoolReferenceName === 'string' && !sqlPools.includes(sqlPoolReferenceName)) {
                    sqlPools.push(sqlPoolReferenceName);
                    const sqlPoolFile: IFile = {
                        payload: JSON.stringify({
                                name: sqlPoolReferenceName,
                                properties: {},
                            }, null, '\t'),
                        fileName: `${sqlPoolReferenceName}.json`
                    };
                    sqlPoolFiles.push(sqlPoolFile);
                }
            });
        });

        const datasetFiles: string[] = await LocalFileUtils.readArtifactFiles(loggerService, rootFolder, 'dataset');
        datasetFiles.forEach((datasetJson) => {
            const datasetObject = JSON.parse(datasetJson);
            if (datasetObject.properties.type === 'SqlPoolTable') {
                const sqlPoolReferenceName = datasetObject.properties.sqlPool?.referenceName;
                if (sqlPoolReferenceName && typeof sqlPoolReferenceName === 'string' && !sqlPools.includes(sqlPoolReferenceName)) {
                    sqlPools.push(sqlPoolReferenceName);
                    const sqlPoolFile: IFile = {
                        payload: JSON.stringify({
                                name: sqlPoolReferenceName,
                                properties: {},
                            }, null, '\t'),
                        fileName: `${sqlPoolReferenceName}.json`
                    };
                    sqlPoolFiles.push(sqlPoolFile);
                }
            }
        });

        if (sparkPoolFiles && sparkPoolFiles.length > 0) {
            synapsePools.push({
                poolType: 'bigDataPool',
                poolFiles: sparkPoolFiles
            });
        }

        if (sqlPoolFiles && sqlPoolFiles.length > 0) {
            synapsePools.push({
                poolType: 'sqlPool',
                poolFiles: sqlPoolFiles
            });
        }

        return synapsePools.filter((f) => !!f);
    }

    private static async readArtifactFiles(loggerService: LoggerService, rootFolder: string, artifactType: string = null): Promise<string[]> {
        let fileResources: string[] = [];
        const promises: Array<Promise<string>> = [];
        const filePaths = await this.getArtifactFiles(loggerService, rootFolder, artifactType);
        filePaths.forEach((filePath) => promises.push(new Promise((resolve, reject) => {
            readFile(filePath, 'utf8', (error, data) => {
                if (error) {
                    loggerService.warn(this.className, `Unable to read file: ${filePath}, error: ${convertObjectToString(error)}`);
                    reject(error);
                    return;
                }
                resolve(data);
            });
        })));
        fileResources = await Promise.all(promises) as string[];
        return fileResources;
    }

    private static async getArtifactFiles(loggerService: LoggerService, rootPath: string, artifactType: string = null): Promise<string[]> {
        const files: string[] = [];
        const folderPath = normalizePath(artifactType ? joinPaths(rootPath, artifactType) : rootPath);
        loggerService.log(this.className, `Reading ${artifactType} files at : ${folderPath}`);
        try {
            const dirents = (await promisify(readdir)(folderPath, { encoding: 'utf8', withFileTypes: true }) || []);
            for (const dirent of dirents) {
                const fullPath = normalizePath(joinPaths(folderPath, dirent.name));
                if (dirent.isDirectory()) {
                    const nestedFiles = await this.getArtifactFiles(loggerService, fullPath);
                    files.push(...nestedFiles);
                } else {
                    files.push(fullPath);
                }
            }
        }  catch (err) {
            loggerService.warn(this.className, `Unable to read files for: ${artifactType}, error: ${err}`);
        }

        return files;
    }
}
